import type { Node as PMNode } from '@tiptap/pm/model'
import { numberLevels } from '../../lib/sections'

/** One heading of the live document, with the ProseMirror range of its whole subtree. */
export interface PmHeading {
  /** ATX level 1..6, straight off the heading node's `level` attribute. */
  level: number
  /** Position directly BEFORE the heading node. Inclusive. */
  from: number
  /** Position before the next heading of level <= this one, else the end of the doc.
   *  Exclusive. INCLUDES descendants — the subtree IS the section. */
  to: number
  /** Rendered heading text — the § title. */
  title: string
}

/**
 * The web's level source, mirroring backend/domain/sections.ts#parseSections in ProseMirror
 * position space. Only TOP-LEVEL children of the doc count as headings, which is congruent
 * with the markdown scanner's "the first non-space char must be #" rule (it likewise excludes
 * `> ## quoted` and `- ## listed`), and a § runs from its heading through to the next heading
 * of level <= its own.
 *
 * Derived from `doc` alone — never from markdown. That keeps it synchronous and cheap enough
 * to run per transaction, and immune to RichEditor's setContent reparse (it derives positions
 * rather than mapping them).
 */
export function pmHeadings(doc: PMNode): PmHeading[] {
  const heads: { level: number; from: number; title: string }[] = []
  doc.forEach((node, offset) => {
    if (node.type.name !== 'heading') return
    heads.push({ level: Number(node.attrs.level) || 1, from: offset, title: node.textContent.trim() })
  })

  return heads.map((h, idx) => {
    let to = doc.content.size
    for (let j = idx + 1; j < heads.length; j++) {
      if (heads[j]!.level <= h.level) {
        to = heads[j]!.from
        break
      }
    }
    return { level: h.level, from: h.from, to, title: h.title }
  })
}

/** The human-lock field a prose editor publishes in awareness (lock conflicts, AB#298). */
export interface EditSection {
  /** Outline number, offset-aware — the same string a claim's `section` carries. */
  section: string
  /** The § heading text. */
  title: string
}

/**
 * The DEEPEST § holding position `pos`, numbered under the part's `offset` exactly as
 * SectionNumbers (and the backend's claims) number it. Null when `pos` sits before the first
 * heading, or when the offset is not known yet — a guessed 0 would name the wrong §.
 */
export function editSectionAt(doc: PMNode, pos: number, offset: number | null): EditSection | null {
  if (offset === null) return null
  const heads = pmHeadings(doc)
  const numbers = numberLevels(
    heads.map((h) => h.level),
    offset,
  )
  // Ancestors come before their descendants, so the last § containing `pos` is the deepest.
  let found: EditSection | null = null
  for (let idx = 0; idx < heads.length; idx++) {
    const h = heads[idx]!
    if (pos >= h.from && (pos < h.to || h.to === doc.content.size)) {
      found = { section: numbers[idx]!, title: h.title }
    }
  }
  return found
}
